import * as raf from 'raf'
import * as React from 'react'
import * as VisibilitySensor from 'react-visibility-sensor'

import { reportAudioPlayClick } from '../../../common/analytics/resultAnalytics'
import axiosNonCors from '../../../common/utils/axios/axiosNonCors'
import { getOffsetX } from '../../../common/utils/domEvents'
import { logError } from '../../../common/utils/errors'
import pad from '../../../common/utils/pad'
import { getMinutesSecondsTime } from '../../../common/utils/time'
import SpinnerIcon from '../icons/loadingSpinner'
// TODO: these should be renamed to "pause" and "play" once the
// originals have been removed. LJ
import PauseIcon from '../icons/pauseUpdated'
import PlayIcon from '../icons/playUpdated'
import Waveform from './waveform'

import * as styles from './audioPlayer.module.css'

export interface ILength {
  minutes: number
  hours: number
  seconds: number
}

interface IProps {
  id: number
  length: ILength
  src: string
  onPlay: () => void
  isActive: boolean
  viewType: string
  searchAudioMuted: boolean
  searchAudioVolumeLevel: number
}

interface IState {
  playing: boolean
  bufferProgress: number
  playBarProgress: number
  expectedPlayBarProgress: any
  dragging: boolean
  loading: boolean
  seen: boolean
  waveformData: any
  playPromise: any
  preload: string
  hasPlayed: boolean
}

export default class AudioPlayer extends React.Component<IProps, IState> {
  private unmounted: boolean
  private audioElement: HTMLAudioElement
  private waveformContainerElement: HTMLDivElement
  private buttonElement: HTMLButtonElement

  constructor(props: IProps) {
    super(props)

    this.state = {
      playing: false,
      bufferProgress: 0,
      playBarProgress: 0,
      expectedPlayBarProgress: null,
      dragging: false,
      loading: false,
      seen: false,
      waveformData: null,
      playPromise: null,
      preload: 'none',
      hasPlayed: false
    }

    this.togglePlay = this.togglePlay.bind(this)
    this.onAudioBuffer = this.onAudioBuffer.bind(this)
    this.onAudioProgress = this.onAudioProgress.bind(this)
    this.onWaveformDragStart = this.onWaveformDragStart.bind(this)
    this.onWaveformDragEnd = this.onWaveformDragEnd.bind(this)
    this.onWaveformDrag = this.onWaveformDrag.bind(this)
    this.onSeeked = this.onSeeked.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.onDurationChange = this.onDurationChange.bind(this)
    this.onPlay = this.onPlay.bind(this)
    this.onPause = this.onPause.bind(this)
    this.onElementVisible = this.onElementVisible.bind(this)
    this.setAudioElementRef = this.setAudioElementRef.bind(this)
    this.setWaveformContainerElementRef = this.setWaveformContainerElementRef.bind(
      this
    )
    this.setButtonElementRef = this.setButtonElementRef.bind(this)
  }

  public componentWillUnmount() {
    this.unmounted = true
  }

  public componentDidMount() {
    if (this.audioElement) {
      this.updateAudioVolume()
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    const { searchAudioVolumeLevel } = this.props
    if (this.audioElement) {
      if (prevProps.isActive && !this.props.isActive) {
        this.tryPause()
      }
      if (searchAudioVolumeLevel !== prevProps.searchAudioVolumeLevel) {
        this.updateAudioVolume()
      }
    }
  }

  public render() {
    const {
      playing,
      loading,
      seen,
      waveformData,
      bufferProgress,
      playBarProgress,
      preload,
      hasPlayed
    } = this.state
    const { src, length, viewType } = this.props
    let buttonIcon = null
    if (loading) {
      buttonIcon = <SpinnerIcon className={styles.icon} />
    } else {
      if (playing) {
        buttonIcon = (
          <PauseIcon
            className={styles.icon}
            data-test-selector="audioplayerPauseButton"
          />
        )
      } else {
        buttonIcon = <PlayIcon className={styles.icon} />
      }
    }

    const trackLengthHours = length && length.hours ? `${length.hours}:` : ''
    const trackLength = length
      ? `${trackLengthHours}${pad(length.minutes, 2)}:${pad(length.seconds, 2)}`
      : null
    return (
      <div className={styles.spaceBetween}>
        <VisibilitySensor onChange={this.onElementVisible} active={!seen} />
        <audio
          ref={this.setAudioElementRef}
          preload={preload}
          onPlay={this.onPlay}
          onPause={this.onPause}
          onDurationChange={this.onDurationChange}
          onProgress={this.onAudioBuffer}
          onSeeked={this.onSeeked}
          onCanPlay={this.onSeeked}
          muted={this.props.searchAudioMuted}
        >
          <source src={src} type="audio/mpeg" />
        </audio>
        <button
          ref={this.setButtonElementRef}
          onClick={this.togglePlay}
          onTouchEnd={this.onTouchEnd}
          className={styles.button}
        >
          {buttonIcon}
        </button>
        <div
          role="group"
          data-test-selector="waveform-container"
          ref={this.setWaveformContainerElementRef}
          className={styles.waveformContainer}
          onMouseDown={this.onWaveformDragStart}
          onMouseUp={this.onWaveformDragEnd}
          onMouseMove={this.onWaveformDrag}
          onTouchStart={this.onWaveformDragStart}
          onTouchEnd={this.onWaveformDragEnd}
          onTouchMove={this.onWaveformDrag}
        >
          {waveformData && (
            <React.Fragment>
              <Waveform
                data={waveformData}
                colour="hsl(0, 0%, 80%)"
                progress={1}
                viewType={viewType}
              />
              <Waveform
                data={waveformData}
                colour="hsl(0, 0%, 60%)"
                progress={hasPlayed ? bufferProgress : 0}
                viewType={viewType}
              />
              <Waveform
                data={waveformData}
                colour="hsl(87, 58%, 60%)"
                progress={playBarProgress}
                viewType={viewType}
              />
            </React.Fragment>
          )}
          <div
            style={{
              width: `${playBarProgress * 100}%`,
              height: '100%',
              position: 'absolute',
              boxShadow: '2px 0px 0px hsl(87, 58%, 40%)',
              display: playBarProgress > 0 ? undefined : 'none'
            }}
          >
            <div className={styles.timeContainer}>
              {this.audioElement
                ? getMinutesSecondsTime(this.audioElement.currentTime)
                : ''}
            </div>
          </div>
        </div>
        <span className={styles.audioLength}>{trackLength}</span>{' '}
      </div>
    )
  }

  private updateAudioVolume() {
    const { searchAudioVolumeLevel } = this.props
    this.audioElement.volume = searchAudioVolumeLevel / 100
  }

  private onElementVisible(isVisible: boolean) {
    const { src } = this.props
    const { seen } = this.state
    if (!seen && isVisible) {
      this.setState({ seen: true, preload: 'metadata' })
      // We get a url that looks like the following: https://0.s3.envato.com/files/88545/preview.mp3?foo=bar
      // This regex will extract the 88545/preview.mp3 part from it and send that to tsunami
      const mp3UrlRegex = /^.*files\/(.*\.mp3)/g
      const mp3PathGroup = mp3UrlRegex.exec(src)
      let mp3Path
      if (mp3PathGroup && mp3PathGroup.length >= 1) {
        mp3Path = mp3PathGroup[1]
      }

      if (mp3Path) {
        axiosNonCors
          .get(`https://waveform.customer.envato.com/tsunami/${mp3Path}`)
          .then((response: any) => {
            if (!this.unmounted) {
              this.setState({ waveformData: response.data })
            }
          })
          .catch(logError)
      }
    }
  }

  private onPause() {
    this.setState({ playing: false })
  }

  private onPlay() {
    const { id } = this.props
    reportAudioPlayClick(id)
    this.props.onPlay()
    this.setState({ playing: true, hasPlayed: true })
    raf(this.onAudioProgress)
  }

  private onTouchEnd(event: React.TouchEvent) {
    event.preventDefault()
    this.buttonElement.focus()
    this.togglePlay()
  }

  private tryPause() {
    if (this.state.playPromise) {
      this.state.playPromise.then(() => {
        if (this.audioElement) {
          this.audioElement.pause()
        }
      })
    }
  }

  private tryPlay() {
    if (this.audioElement) {
      /*
        For some old browsers (Safari 9) play() does not return promise but undefined.
        Let's handle that case by returning resolved promise instead.
       */
      return (this.audioElement.play() || Promise.resolve()).catch(error => {
        // Throw error only if HTMLMediaElement still exists, if it's unmounted let's ignored it.
        // HTMLMediaElement internally call pause when removed from DOM. If media is still loading when unmounted
        // it caused an error "play() interrupted by pause()". There is no way to wait for media to play in the process of
        // unmounting
        if (this.audioElement) {
          throw error
        }
      })
    }

    return
  }

  private onDurationChange() {
    if (this.state.expectedPlayBarProgress) {
      this.audioElement.currentTime =
        this.state.expectedPlayBarProgress * this.audioElement.duration
    }
  }

  private onSeeked() {
    this.showLoading()
  }

  private onWaveformDragStart(event: React.MouseEvent | React.TouchEvent) {
    const progress =
      getOffsetX(event) / this.waveformContainerElement.clientWidth
    this.setState({ dragging: true, playBarProgress: progress })
  }

  private onWaveformDragEnd(event: React.MouseEvent | React.TouchEvent) {
    const { playBarProgress } = this.state

    event.preventDefault()

    this.setState({ dragging: false })

    if (this.audioElement.duration) {
      this.audioElement.currentTime =
        playBarProgress * this.audioElement.duration
      this.setState({
        playPromise: this.tryPlay()
      })

      this.showLoading()
    } else {
      this.setState(
        {
          expectedPlayBarProgress: playBarProgress
        },
        () => {
          this.setState({
            playPromise: this.tryPlay()
          })
          this.showLoading()
        }
      )
    }
  }

  private onWaveformDrag(event: React.MouseEvent | React.TouchEvent) {
    const { dragging } = this.state
    if (dragging) {
      this.setState({
        playBarProgress:
          getOffsetX(event) / this.waveformContainerElement.clientWidth
      })
    }
  }

  private onAudioBuffer() {
    if (this.audioElement.buffered.length > 0 && this.audioElement.duration) {
      let percentLoaded =
        this.audioElement.buffered.end(this.audioElement.buffered.length - 1) /
        this.audioElement.duration
      if (percentLoaded > 1) {
        percentLoaded = 1
      }
      this.setState({ bufferProgress: percentLoaded })
    }
  }

  private onAudioProgress() {
    if (this.state.playing) {
      if (!this.state.dragging && this.audioElement) {
        this.setState({
          playBarProgress:
            this.audioElement.currentTime / this.audioElement.duration
        })
      }
      raf(this.onAudioProgress)
    }
  }

  private showLoading() {
    if (this.audioElement.seeking || this.audioElement.readyState < 3) {
      this.setState({ loading: true })
    } else {
      this.setState({ loading: false })
    }
  }

  private togglePlay() {
    const { playing } = this.state
    if (this.audioElement) {
      const isTrulyPlaying =
        this.audioElement.currentTime > 0 &&
        !this.audioElement.paused &&
        !this.audioElement.ended &&
        this.audioElement.readyState > 2
      if (playing && isTrulyPlaying) {
        this.tryPause()
      } else if (!isTrulyPlaying) {
        this.setState({
          playPromise: this.tryPlay()
        })
        this.showLoading()
      }
    }
  }

  private setAudioElementRef(audioElement: HTMLAudioElement) {
    this.audioElement = audioElement
  }

  private setWaveformContainerElementRef(waveformElement: HTMLDivElement) {
    this.waveformContainerElement = waveformElement
  }

  private setButtonElementRef(buttonElement: HTMLButtonElement) {
    this.buttonElement = buttonElement
  }
}
